/**
 * HeroFloatingMail — ambient floating mail icons on the SIDES of
 * the hero section. Stays out of the centre content column so it
 * never overlaps text, buttons, or the trust badges. Reacts to the
 * user's cursor with a soft repulsion so a hovered icon bounces
 * off and drifts away.
 *
 * Sizing contract:
 *   - Total pieces is intentionally tiny (default 7) so ~3–4 are
 *     visible in the viewport at a time. The rest sit further down
 *     the hero band.
 *   - Each piece is locked to either the LEFT or RIGHT side band
 *     (~14% of viewport width, min 120 px). Velocities never carry
 *     a piece across into the content column.
 *   - Lives inside the hero `<section>` (overflow:hidden), so
 *     scrolling past the hero hides every piece automatically.
 *
 * Performance:
 *   - 7 pieces × rAF transform writes. No React state in hot loop.
 *   - O(n) mouse-repel only.
 *   - `prefers-reduced-motion` skips the layer entirely.
 */
import { useEffect, useRef, useState } from "react";

const BRAND_COLORS = [
  "#9200C7", "#0083D3", "#62BB46", "#FF6B00",
  "#C026D3", "#EC4899", "#0EA5E9",
];

const REPEL_RADIUS = 110;
const REPEL_STRENGTH = 1400;
const MAX_SPEED = 55;
const MIN_SPEED = 10;

const r = (a, b) => a + Math.random() * (b - a);

const MAIL_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" stroke-linejoin="round" width="100%" height="100%">' +
  '<rect width="20" height="16" x="2" y="4" rx="2"/>' +
  '<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>';

// Compute the two side bands for the current container width.
function sideBands(W) {
  const bandWidth = Math.max(120, Math.round(W * 0.14));
  return {
    bandWidth,
    leftMin: 0,
    leftMax: bandWidth,
    rightMin: W - bandWidth,
    rightMax: W,
  };
}

export default function HeroFloatingMail({ count = 7, delay = 3250 }) {
  const [active, setActive] = useState(false);
  const containerRef = useRef(null);

  // Delay activation so the intro swarm finishes first.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip the ambient swarm on mobile — limited screen real-estate
    // means even a side-band swarm competes with content reading. The
    // intro burst still plays so mobile users get the wow moment.
    const isMobile = window.matchMedia?.("(max-width: 767px)").matches;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || isMobile) return;
    const t = setTimeout(() => setActive(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    let W = container.clientWidth;
    let H = container.clientHeight;
    if (W < 20 || H < 20) return;

    let bands = sideBands(W);

    // ── Build pieces — half on the left side, half on the right ─
    const pieces = [];
    for (let i = 0; i < count; i++) {
      const onLeft = i % 2 === 0;
      const size = Math.round(r(28, 42));
      const el = document.createElement("span");
      el.className = "mail-floating-piece";
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.color = BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)];
      el.style.opacity = "0";
      el.innerHTML = MAIL_SVG;
      container.appendChild(el);
      const angle = r(0, Math.PI * 2);
      const speed = r(14, 26);
      pieces.push({
        side: onLeft ? "L" : "R",
        x: onLeft
          ? r(bands.leftMin + 20, bands.leftMax - 20)
          : r(bands.rightMin + 20, bands.rightMax - 20),
        y: r(60, Math.max(120, H - 60)),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: r(0, 360),
        rotV: r(-18, 18),
        size,
        radius: size * 0.45,
        el,
        fadeStart: i * 0.06,
      });
    }

    // ── Mouse tracking ─────────────────────────────────────────
    const mouse = { x: -9999, y: -9999, inside: false };
    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouse.x = x;
      mouse.y = y;
      mouse.inside = x > -40 && x < W + 40 && y > -40 && y < H + 40;
    };
    const onLeave = () => { mouse.inside = false; };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);

    const onResize = () => {
      W = container.clientWidth;
      H = container.clientHeight;
      bands = sideBands(W);
      for (const p of pieces) {
        // Re-clamp into the assigned band after resize.
        const min = p.side === "L" ? bands.leftMin + p.radius : bands.rightMin + p.radius;
        const max = p.side === "L" ? bands.leftMax - p.radius : bands.rightMax - p.radius;
        if (p.x < min) p.x = min;
        if (p.x > max) p.x = max;
        if (p.y < p.radius) p.y = p.radius;
        if (p.y > H - p.radius) p.y = H - p.radius;
      }
    };
    window.addEventListener("resize", onResize);

    // ── Pause physics when the hero scrolls offscreen — saves
    //    CPU/GPU cycles for the rest of the page's scroll perf.
    //    IntersectionObserver is widely supported (Safari 12.1+,
    //    Chrome 51+, Firefox 55+, Edge 15+); the try/catch is a
    //    belt-and-braces guard so very old browsers still get the
    //    layer (just without the offscreen pause).
    let onScreen = true;
    let io = null;
    try {
      if (typeof IntersectionObserver !== "undefined") {
        io = new IntersectionObserver(
          (entries) => { onScreen = entries[0].isIntersecting; },
          { threshold: 0 },
        );
        io.observe(container);
      }
    } catch (_e) { /* fall through, layer just keeps running */ }

    // ── Physics loop ──────────────────────────────────────────
    const start = performance.now() / 1000;
    let prev = start;
    let raf = 0;

    const step = () => {
      const now = performance.now() / 1000;
      const dt = Math.min(0.05, now - prev);
      prev = now;
      const elapsed = now - start;

      if (!onScreen) {
        // Skip integration + DOM writes while offscreen so scrolling
        // the rest of the page stays buttery.
        raf = requestAnimationFrame(step);
        return;
      }

      for (const p of pieces) {
        // Mouse repulsion (only if cursor is in this piece's band)
        if (mouse.inside) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < REPEL_RADIUS * REPEL_RADIUS && d2 > 0.5) {
            const d = Math.sqrt(d2);
            const t = 1 - d / REPEL_RADIUS;
            const k = REPEL_STRENGTH * t * dt;
            p.vx += (dx / d) * k;
            p.vy += (dy / d) * k;
            if (d < p.radius + 14) {
              p.rotV += (Math.random() - 0.5) * 180;
            }
          }
        }

        // Damping so kicks decay back to gentle drift
        p.vx *= 0.97;
        p.vy *= 0.97;

        // Clamp speed
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy) || 0.001;
        if (speed < MIN_SPEED) {
          p.vx = (p.vx / speed) * MIN_SPEED;
          p.vy = (p.vy / speed) * MIN_SPEED;
        } else if (speed > MAX_SPEED) {
          p.vx = (p.vx / speed) * MAX_SPEED;
          p.vy = (p.vy / speed) * MAX_SPEED;
        }

        // Integrate
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.rotV * dt;

        // Side-band walls (key: pieces NEVER cross into content)
        const xMin = (p.side === "L" ? bands.leftMin : bands.rightMin) + p.radius;
        const xMax = (p.side === "L" ? bands.leftMax : bands.rightMax) - p.radius;
        if (p.x < xMin) { p.x = xMin; p.vx = Math.abs(p.vx); }
        if (p.x > xMax) { p.x = xMax; p.vx = -Math.abs(p.vx); }
        if (p.y < p.radius) { p.y = p.radius; p.vy = Math.abs(p.vy); }
        if (p.y > H - p.radius) { p.y = H - p.radius; p.vy = -Math.abs(p.vy); }

        // Fade in
        let alpha = 1;
        const age = elapsed - p.fadeStart;
        if (age < 0.7) alpha = Math.max(0, age / 0.7);
        // Slightly muted so they read as ambient decoration.
        p.el.style.opacity = String(alpha * 0.5);
        p.el.style.transform =
          `translate3d(${p.x - p.size / 2}px, ${p.y - p.size / 2}px, 0) rotate(${p.rot}deg)`;
      }

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", onResize);
      for (const p of pieces) {
        try { p.el.remove(); } catch (_e) { /* noop */ }
      }
    };
  }, [active, count]);

  return (
    <div
      ref={containerRef}
      className="mail-floating-layer"
      aria-hidden="true"
      data-testid="hero-floating-mail-layer"
    />
  );
}
